/**
 * The WHO HAS ACCESS group line (PRD-6.1, SE-8359 / PV17): "Everyone at
 * {organization}" with its member count and role, a `Can edit` / `Can view`
 * Select — EDITOR or VIEWER only, never OWNER (R06 §2) — and Revoke, both
 * disabled while a write is in flight. The same visual language as the
 * person row (`ShareAccessRow`), with a group mark in place of the avatar.
 * @module features/scouting/components/molecules/ShareGroupAccessRow/ShareGroupAccessRowView
 */

import { ChevronDownIcon, UsersIcon, XIcon } from "lucide-react-native";
import { memo } from "react";

import { Avatar } from "@/components/atoms/Avatar";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectScrollView,
  SelectTrigger,
} from "@/components/atoms/Select";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { SHARE_ROLE_OPTIONS } from "@/features/scouting/utils/shareWatchlistGrants";

import { TEST_ID_WATCHLIST_SHARE } from "e2e/testId";

/** Props for {@link ShareGroupAccessRowView}. */
export interface ShareGroupAccessRowViewProps {
  /** Group grant id (test identity). */
  readonly groupGrantId: string;
  /** "Everyone at {organization}". */
  readonly label: string;
  /** "183 people · Viewer" line under the label. */
  readonly detail: string;
  /** Current role as a Select value. */
  readonly role: string;
  /** Label of the selected role option ("Can edit" / "Can view"). */
  readonly roleOptionLabel: string;
  /** Whether a role change or revoke is in flight. */
  readonly isPending: boolean;
  /** Role Select change handler. */
  readonly onRoleChange: (value: string) => void;
  /** Revoke handler. */
  readonly onRevoke: () => void;
}

/**
 * Renders the group line.
 * @param root0 - View props
 * @param root0.groupGrantId - Group grant id
 * @param root0.label - Group label
 * @param root0.detail - Member count and role line
 * @param root0.role - Current role value
 * @param root0.roleOptionLabel - Selected option label
 * @param root0.isPending - Write in flight
 * @param root0.onRoleChange - Role change handler
 * @param root0.onRevoke - Revoke handler
 * @returns The rendered line.
 */
const ShareGroupAccessRowView = ({
  groupGrantId,
  label,
  detail,
  role,
  roleOptionLabel,
  isPending,
  onRoleChange,
  onRevoke,
}: ShareGroupAccessRowViewProps) => (
  <Stack
    direction="horizontal"
    testID={TEST_ID_WATCHLIST_SHARE.groupRowFor(groupGrantId)}
    className="items-center gap-3 py-2"
  >
    <Avatar size="sm" className="bg-surface-muted">
      <Icon as={UsersIcon} size="sm" className="text-content-secondary" />
    </Avatar>
    <Stack className="min-w-0 flex-1">
      <Text
        variant="body-strong"
        className="text-content-primary"
        numberOfLines={1}
      >
        {label}
      </Text>
      <Text variant="caption" className="text-content-muted">
        {detail}
      </Text>
    </Stack>
    <Select
      selectedValue={role}
      onValueChange={onRoleChange}
      isDisabled={isPending}
    >
      <SelectTrigger
        testID={TEST_ID_WATCHLIST_SHARE.groupRoleSelectFor(groupGrantId)}
        accessibilityLabel={`Change the role of ${label}`}
        variant="outline"
        size="sm"
        className="h-9 rounded-sm border-outline-default px-3"
      >
        <SelectInput
          value={roleOptionLabel}
          placeholder="Role"
          className="text-body"
        />
        <SelectIcon as={ChevronDownIcon} className="text-content-muted" />
      </SelectTrigger>
      {/* useRNModal: same reason as ShareAccessRowView — inside the mobile
          share sheet's RN Modal the root-overlay Select sheet is unreachable. */}
      <SelectPortal useRNModal>
        <SelectBackdrop />
        <SelectContent>
          <SelectScrollView>
            {/* The fixed EDITOR / VIEWER pair (R06 §2) is the View's own:
                the Container hands down primitives and callbacks only. */}
            {SHARE_ROLE_OPTIONS.map(option => (
              <SelectItem
                key={option.value}
                label={option.label}
                value={option.value}
              />
            ))}
          </SelectScrollView>
        </SelectContent>
      </SelectPortal>
    </Select>
    <Pressable
      testID={TEST_ID_WATCHLIST_SHARE.groupRevokeFor(groupGrantId)}
      onPress={onRevoke}
      disabled={isPending}
      accessibilityRole="button"
      accessibilityLabel={`Remove ${label}`}
      className="web:hover:bg-surface-raised size-9 items-center justify-center rounded-sm border border-outline-default"
    >
      <Icon as={XIcon} size="sm" className="text-content-secondary" />
    </Pressable>
  </Stack>
);

ShareGroupAccessRowView.displayName = "ShareGroupAccessRowView";

export default memo(ShareGroupAccessRowView);
